import { useMsal } from '@azure/msal-react';
import { Avatar, Button, Stack, Typography } from '@mui/material';
import { NavigationBar } from 'customer_portal-ui-shared';
import { useEffect, useState } from 'react';
import { IUser } from '../../contracts/user.contract';
import { UserFetch } from '../../features/user.feature';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { ConfigService, UserService } from '../../services';
import LogoutIcon from './log-out-icon.png';
import UserInfoIcon from './user-icon.png';
import { useNavigate } from 'react-router-dom';

const HeaderUserInfo = () => {
    const { instance } = useMsal();

    const dispatch = useAppDispatch();
    const currentUser = useAppSelector((state) => state.User.CurrentUser);

    const [isImpersonated, setIsImpersonated] = useState<boolean>(false);

    useEffect(() => {
        dispatch(UserFetch.GetCurrentUser());

        const user = sessionStorage.getItem('impersonatedUser');
        if (!user) return;
        setIsImpersonated(true);
    }, []);

    const handleLogout = () => {
        instance.logoutRedirect();
    };

    return (
        <>
            <Stack spacing={0} direction={'row'} alignItems={'center'} justifyContent={'flex-end'} style={{ backgroundColor: '#002A4D', height: '100%', paddingRight: '10px' }}>
                {currentUser && (
                    <Button
                        variant={'contained'}
                        size="small"
                        startIcon={<Avatar src={UserInfoIcon} sx={{ height: '26px', width: '26px' }} />}
                        sx={{ textTransform: 'none', fontWeight: 'bold', boxShadow: 'none' }}
                    >
                        {`${isImpersonated ? currentUser.Email : currentUser.Company}`}
                    </Button>
                )}

                <Button
                    variant={'contained'}
                    onClick={() => handleLogout()}
                    size="small"
                    startIcon={<Avatar src={LogoutIcon} sx={{ height: '26px', width: '26px' }} />}
                    sx={{ textTransform: 'none', fontWeight: 'bold', boxShadow: 'none' }}
                >
                    Logout
                </Button>
            </Stack>
        </>
    );
};

const SubHeader = () => {
    const navigate = useNavigate();
    const [selectedMenu, setSelectedMenu] = useState<string>('PSC');

    const getNavigationItems = () => {
        // Navigation Items
        const navigationItems: any = {
            PSC: {
                selectBy: 'PSC',
                action: () => {
                    setSelectedMenu('PSC');
                    navigate('/');
                },
                label: 'Port State Control',
                width: 200
            }
        };

        if (ConfigService.appConfig.enableBenchmarking) {
            navigationItems['Benchmarking'] = {
                selectBy: 'Benchmarking',
                action: () => {
                    setSelectedMenu('Benchmarking');
                    navigate('/benchmarking');
                },
                label: 'Fleet Performance',
                width: 200
            };
        }

        return navigationItems;
    };

    return (
        <>
            <NavigationBar data={getNavigationItems()} selected={selectedMenu} history={1} />
        </>
    );
};

const Header = () => {
    const [headerHeight, setHeaderHeight] = useState<string | number>('auto');
    const [headerProps, setHeaderProps] = useState({
        width: '100%',
        title: 'Header',
        scrolling: 'auto',
        frameBorder: '0'
    });

    const [currentUser, setCurrentUser] = useState<IUser | undefined>(undefined);
    const [impersonatedUser, setImpersonatedUser] = useState<string | undefined>(undefined);

    useEffect(() => {
        (async () => {
            await getCurrentUser();
            const user = sessionStorage.getItem('impersonatedUser');

            if (!user) return;
            setImpersonatedUser(user);
        })();
    }, []);

    const getCurrentUser = async () => {
        const user = await UserService.getCurrentUser();
        if (!user) return;
        setCurrentUser(user);
    };

    const dispatchMessage = (payload: any) => {
        const headerContent: any = document.getElementById('ifrmHeader');
        headerContent?.contentWindow?.postMessage(payload, '*');
    };

    const messageListener = (e: any) => {
        if (e.data?.event) {
            const isResize = e.data?.event === 'resizeheader';
            const isProps = e.data?.event === 'headerProps' && e.data?.props && typeof e.data.props === 'object';
            isResize && setHeaderHeight(e.data.height);
            isProps && setHeaderProps((currentProps) => ({ ...currentProps, ...e.data.props }));
        }
    };

    const clickListener = () => setHeaderHeight(70);
    const resizeListener = () => dispatchMessage({ windowResize: { width: window.innerWidth } });

    const triggerOnLoad = () => {
        const user = 'user';
        const host = window.location;
        const environment = 'https://dev-smapp.apps.eagle.org/';
        const copyright = `ABS &copy; ${new Date().getFullYear()} All Rights Reserved`;
        const buildTime = 'buildTime';
        const payLoad = { headerLoaded: true, copyright, buildTime, user, host, environment };
        dispatchMessage(JSON.parse(JSON.stringify(payLoad)));
    };

    const listeners = [
        ['message', messageListener],
        ['resize', resizeListener],
        ['click', clickListener]
    ];

    useEffect(() => {
        listeners.forEach((l: any) => window.addEventListener(l[0], l[1]));
        return () => listeners.forEach((l: any) => window.removeEventListener(l[0], l[1]));
    }, []);

    return (
        <>
            <div id="HeaderWrapper" style={{ height: '70px' }}>
                <iframe
                    src={ConfigService.appConfig.aemHeaderUrl}
                    id="ifrmHeader"
                    height={headerHeight}
                    onLoad={triggerOnLoad}
                    {...headerProps}
                    style={{ position: 'fixed', width: '100%', top: 0, left: 0, zIndex: 1042 }}
                />
            </div>

            <div style={{ position: 'fixed', height: '70px', top: 0, right: 0, zIndex: 1050, minWidth: '260px' }}>
                <HeaderUserInfo />
            </div>

            <SubHeader />

            {impersonatedUser && currentUser && (
                <Stack direction={'row'} alignContent={'center'} justifyContent={'center'} alignItems={'center'} style={{ backgroundColor: '#DA1F33', color: '#ffffff', padding: 5 }} gap={2}>
                    <Typography>
                        Impersonating <b>{currentUser.Company}</b>
                    </Typography>
                </Stack>
            )}
        </>
    );
};

export default Header;
